import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Grid, Typography, Button, TextField, InputAdornment, Paper, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, alpha,
  MenuItem, Select, FormControl, IconButton, Tooltip, Switch, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EventIcon from "@mui/icons-material/Event";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import LayersIcon from "@mui/icons-material/Layers";
import BoltIcon from "@mui/icons-material/Bolt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import {
  useGetMenuSchedulesQuery,
  useUpdatePlaylistGroupItemMutation,
  useRemovePlaylistFromGroupMutation,
  useLazyPreviewScheduleQuery,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import {
  IMenuScheduleRow, MenuScheduleStatus, MenuType,
} from "../../../Api/Interface/api.interface";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import Pagination from "../../../Components/Pagination";
import {
  MENU_TYPE_LABELS, WEEK_DAYS, to12h,
} from "../../Playlist/Edit/menuScheduleShared";

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) === 1 ? "" : "s"} ago`;
};

/** Human "Mon, Tue · 3:00 PM–9:00 PM · from…to…" summary for a schedule row. */
const scheduleSummary = (r: IMenuScheduleRow): string => {
  const days = !r.days_of_week
    ? "Every day"
    : WEEK_DAYS.filter((_, i) => (r.days_of_week! & (1 << i)) !== 0).join(", ");
  const time = (!r.start_time && !r.end_time)
    ? "All day"
    : `${to12h(r.start_time)}–${to12h(r.end_time)}`;
  const dates = r.start_date || r.end_date
    ? ` · ${r.start_date ?? "…"}→${r.end_date ?? "…"}` : "";
  return `${days} · ${time}${dates}`;
};

const STATUS_STYLE: Record<MenuScheduleStatus, { bg: string; fg: string; border: string; label: string }> = {
  active:   { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0", label: "Active" },
  upcoming: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe", label: "Upcoming" },
  ended:    { bg: "#f1f5f9", fg: "#64748b", border: "#cbd5e1", label: "Ended" },
  disabled: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca", label: "Disabled" },
};

const MENU_TYPE_ORDER: MenuType[] = [
  "default_menu", "daily_special", "time_based_menu", "holiday_menu",
  "promotional_campaign", "pos_synced_menu", "emergency_override", "standard_playlist",
];

const MenuScheduleList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  // Match the platform convention: content managers may not remove schedules
  // (the backend enforces this too via the PlaylistGroup policy).
  const canDelete = currentUser?.role === "super_admin" || currentUser?.role === "company_admin";

  const [searchQuery, setSearchQuery] = useState("");
  const debounced = useDebounce(searchQuery, 300);
  const [statusFilter, setStatusFilter] = useState<"" | MenuScheduleStatus>("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");
  const [typeFilter, setTypeFilter] = useState<"" | MenuType>("");
  const [screensFilter, setScreensFilter] = useState<"" | "assigned" | "unassigned">("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // "How it works" help panel. Open the first time; remember if dismissed.
  const [showHelp, setShowHelp] = useState<boolean>(
    () => localStorage.getItem("menuScheduleHelpDismissed") !== "1"
  );
  const dismissHelp = () => {
    setShowHelp(false);
    localStorage.setItem("menuScheduleHelpDismissed", "1");
  };
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const { data, isLoading, isError, refetch } = useGetMenuSchedulesQuery(
    companyFilter ? { company_id: Number(companyFilter) } : undefined
  );
  const [updateItem] = useUpdatePlaylistGroupItemMutation();
  const [removeItem] = useRemovePlaylistFromGroupMutation();
  const [runPreview, { data: previewData, isFetching: previewing }] = useLazyPreviewScheduleQuery();

  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const [openDelete, setOpenDelete] = useState(false);
  const [toDelete, setToDelete] = useState<IMenuScheduleRow | null>(null);
  const [previewRow, setPreviewRow] = useState<IMenuScheduleRow | null>(null);

  useEffect(() => { setPage(0); }, [debounced, statusFilter, companyFilter, typeFilter, screensFilter, fromDate, toDate]);

  const rows: IMenuScheduleRow[] = Array.isArray(data?.data)
    ? ((data?.data as unknown) as IMenuScheduleRow[])
    : [];

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hay = `${r.playlist?.name ?? ""} ${r.group_name} ${r.company?.name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter && r.status !== statusFilter) return false;
      if (typeFilter && r.menu_type !== typeFilter) return false;
      if (screensFilter === "assigned" && r.screens_count <= 0) return false;
      if (screensFilter === "unassigned" && r.screens_count > 0) return false;
      // Date-range filter: keep rows whose schedule window overlaps [from, to].
      if (fromDate && r.end_date && r.end_date < fromDate) return false;
      if (toDate && r.start_date && r.start_date > toDate) return false;
      return true;
    });
  }, [rows, debounced, statusFilter, typeFilter, screensFilter, fromDate, toDate]);

  const openPreview = (r: IMenuScheduleRow) => {
    setPreviewRow(r);
    runPreview({ groupId: r.group_id });
  };

  const toggleEnabled = async (r: IMenuScheduleRow) => {
    try {
      await updateItem({
        groupId: r.group_id, itemId: r.id, body: { is_active: !r.is_active, force: true },
      }).unwrap();
      showMessage(r.is_active ? "Schedule disabled" : "Schedule enabled");
      refetch();
    } catch { showErrorMessage("Could not update the schedule."); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await removeItem({ groupId: toDelete.group_id, itemId: toDelete.id }).unwrap();
      showMessage("Menu schedule removed");
      setOpenDelete(false); setToDelete(null);
      refetch();
    } catch { showErrorMessage("Failed to remove"); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const head = ["Schedule", "Group", "Company", "Menu Type", "When", "Screens", "Priority", "Status", "Last Updated"].join(",");
    const body = filtered.map((r) => [
      r.playlist?.name || "", r.group_name, r.company?.name || "",
      r.menu_type ? MENU_TYPE_LABELS[r.menu_type] : "",
      scheduleSummary(r), r.screens_count, r.priority,
      STATUS_STYLE[r.status].label, r.updated_at ?? "",
    ].map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu-schedules-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <PageLoader height="20vh" />;

  const preview = previewData?.data as any;
  const colCount = isSuperAdmin ? 9 : 8;

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <EventIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Menu Schedules
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {rows.length} scheduled menu{rows.length === 1 ? "" : "s"} · when &amp; how playlists become active
            </Typography>
          </Box>
          <TextField
            size="small" placeholder="Search menu schedules..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />
          <Button variant="outlined" size="small"
            onClick={() => setShowHelp((v) => !v)}
            startIcon={<HelpOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: showHelp ? GOLD : BORDER,
              color: showHelp ? NAVY : TEXT_PRIMARY,
              bgcolor: showHelp ? alpha(GOLD, 0.08) : "transparent",
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}>
            How it works
          </Button>
          <Button variant="outlined" size="small"
            onClick={() => setShowFilters((v) => !v)}
            startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: showFilters ? GOLD : BORDER,
              color: showFilters ? NAVY : TEXT_PRIMARY,
              bgcolor: showFilters ? alpha(GOLD, 0.08) : "transparent",
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}>
            Filters
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />
          <Button variant="outlined" size="small" onClick={exportCsv}
            startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: BORDER, color: TEXT_PRIMARY,
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}>
            Export
          </Button>
          <Button variant="contained" color="secondary" disableElevation size="small"
            onClick={() => navigate("/menu-schedules/create")}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}>
            Create Menu Schedule
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3 }}>
        <Collapse in={showHelp} unmountOnExit>
          <Paper elevation={0} sx={{ mt: 3, borderRadius: 2, border: `1px solid ${alpha(GOLD, 0.4)}`, bgcolor: alpha(GOLD, 0.05), overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 1.5, borderBottom: `1px solid ${alpha(GOLD, 0.25)}` }}>
              <HelpOutlineIcon sx={{ fontSize: 20, color: GOLD }} />
              <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                How menu scheduling works
              </Typography>
              <Tooltip title="Hide (you can reopen with “How it works”)">
                <IconButton size="small" onClick={dismissHelp}><CloseIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} /></IconButton>
              </Tooltip>
            </Box>

            <Grid container>
              {/* How resolution works */}
              <Grid item xs={12} md={6} sx={{ p: 2.5, borderRight: { md: `1px solid ${alpha(GOLD, 0.2)}` } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <EventIcon sx={{ fontSize: 18, color: NAVY }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>What decides "what's on screen now"</Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { fontSize: 12.5, color: TEXT_PRIMARY, mb: 0.75, lineHeight: 1.5 } }}>
                  <li>Each screen plays the menus in its assigned <strong>Playlist Group</strong>. The system automatically picks which one is live, using the current time in the company's timezone.</li>
                  <li>A menu is <strong>eligible</strong> when now falls inside its schedule — its <strong>days of the week</strong>, its <strong>date range</strong>, and its <strong>time window</strong>. A menu with none set is always eligible (shown as "Every day · All day"). Overnight windows (e.g. 10 PM–2 AM) are supported.</li>
                  <li>When several are eligible at once, the <strong>highest priority wins</strong>: first by the <strong>Priority</strong> number you set (higher wins), then by menu type.</li>
                  <li>If nothing is scheduled, the <strong>Default Menu</strong> plays as a fallback — a screen is never left blank.</li>
                </Box>
              </Grid>

              {/* How to override */}
              <Grid item xs={12} md={6} sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <LayersIcon sx={{ fontSize: 18, color: NAVY }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>How to override the regular / default menu</Typography>
                </Box>
                <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { fontSize: 12.5, color: TEXT_PRIMARY, mb: 0.75, lineHeight: 1.5 } }}>
                  <li><strong>Add a higher-ranked menu</strong> (e.g. Daily Special, Time-Based, Holiday or Promotional Campaign) and set its days / dates / time window. It takes over during that window; when it ends, the Default Menu resumes automatically.</li>
                  <li>To push one menu above another of the <strong>same type</strong>, give it a higher <strong>Priority</strong> number.</li>
                  <li>For an instant, urgent takeover on a screen (ignores the normal schedule), use an <strong>Emergency Announcement</strong> — it outranks everything until it expires.</li>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                    <BoltIcon sx={{ fontSize: 15, color: GOLD }} />
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: NAVY }}>Precedence (highest wins)</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {["Emergency Announcement", "Promotional Campaign", "Holiday Menu", "Daily Special", "Time-Based Menu", "POS-Synced Menu", "Standard Playlist", "Default Menu"].map((t, i, arr) => (
                      <React.Fragment key={t}>
                        <Chip size="small" label={t} sx={{ height: 20, fontSize: 10, bgcolor: "#fff", border: `1px solid ${BORDER}` }} />
                        {i < arr.length - 1 && <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, alignSelf: "center" }}>›</Typography>}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${alpha(GOLD, 0.2)}`, display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" variant="contained" color="secondary" disableElevation
                onClick={() => navigate("/menu-schedules/create")}
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: 2, color: NAVY_DARK, background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)` }}>
                Create an override schedule
              </Button>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      <Box sx={{ p: 3 }}>
        {showFilters && (
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <FilterListIcon sx={{ fontSize: 18, color: GOLD }} />
              <Typography fontSize={13} fontWeight={700} color={NAVY}>Filters</Typography>
            </Box>
            <Grid container spacing={2}>
              {isSuperAdmin && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Company</Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={companyFilter === "" ? "" : String(companyFilter)}
                      onChange={(e) => setCompanyFilter(e.target.value === "" ? "" : Number(e.target.value))}
                      displayEmpty sx={{ fontSize: 13, borderRadius: 1.5 }}>
                      <MenuItem value="">All companies</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>{c.companyName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Status</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={statusFilter} displayEmpty
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    sx={{ fontSize: 13, borderRadius: 1.5 }}>
                    <MenuItem value="">All statuses</MenuItem>
                    {(["active", "upcoming", "ended", "disabled"] as MenuScheduleStatus[]).map((s) => (
                      <MenuItem key={s} value={s}>{STATUS_STYLE[s].label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Menu Type</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={typeFilter} displayEmpty
                    onChange={(e) => setTypeFilter(e.target.value as any)}
                    sx={{ fontSize: 13, borderRadius: 1.5 }}>
                    <MenuItem value="">All types</MenuItem>
                    {MENU_TYPE_ORDER.map((t) => (
                      <MenuItem key={t} value={t}>{MENU_TYPE_LABELS[t]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Screens</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={screensFilter} displayEmpty
                    onChange={(e) => setScreensFilter(e.target.value as any)}
                    sx={{ fontSize: 13, borderRadius: 1.5 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="assigned">On screens</MenuItem>
                    <MenuItem value="unassigned">No screens</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Active from</Typography>
                <TextField size="small" type="date" fullWidth value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: 1.5 } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>Active to</Typography>
                <TextField size="small" type="date" fullWidth value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: 1.5 } }} />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Paper elevation={0} sx={{
          borderRadius: "12px", border: `1px solid ${BORDER}`,
          overflow: "hidden", boxShadow: "0 4px 12px rgba(11,30,45,0.06)",
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {(
                    [
                      "Schedule",
                      ...(isSuperAdmin ? ["Company"] : []),
                      "Menu Type",
                      "When",
                      "Screens",
                      "Priority",
                      "Status",
                      "Last Updated",
                      "Actions",
                    ] as string[]
                  ).map((h, i, arr) => (
                    <TableCell key={h}
                      sx={{
                        borderBottom: `1px solid ${BORDER}`,
                        background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`,
                        fontSize: 11, fontWeight: 700, color: NAVY,
                        textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5,
                        textAlign: i === arr.length - 1 ? "right" : "left",
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 6, color: TEXT_SECONDARY }}>
                      {isError ? "Error fetching data" : "No menu schedules yet — click \"Create Menu Schedule\""}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const st = STATUS_STYLE[row.status];
                    return (
                      <TableRow key={`${row.group_id}-${row.id}`} sx={{
                        transition: "background 0.15s ease",
                        "&:hover": { bgcolor: "#f8fafc" },
                        opacity: row.is_active ? 1 : 0.7,
                      }}>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{
                              width: 32, height: 32, borderRadius: 1.5,
                              bgcolor: alpha("#0891b2", 0.12),
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <EventIcon sx={{ fontSize: 16, color: "#0891b2" }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}>
                                {row.playlist?.name || "—"}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                                in {row.group_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                            {row.company ? (
                              <Chip size="small" label={row.company.name}
                                sx={{
                                  height: 22, fontSize: 11, fontWeight: 600,
                                  bgcolor: alpha(NAVY, 0.06), color: NAVY,
                                  border: `1px solid ${alpha(NAVY, 0.12)}`,
                                }} />
                            ) : (
                              <Typography component="span" sx={{ fontSize: 13, color: TEXT_SECONDARY }}>—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <Chip size="small"
                            label={row.menu_type ? MENU_TYPE_LABELS[row.menu_type] : "—"}
                            sx={{
                              height: 20, fontSize: 10, fontWeight: 700,
                              bgcolor: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe",
                            }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 12, color: TEXT_SECONDARY, maxWidth: 260 }}>
                          {scheduleSummary(row)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>
                          {row.screens_count}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>
                          {row.priority}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <Chip size="small" label={st.label}
                            sx={{
                              height: 22, fontSize: 11, fontWeight: 700,
                              bgcolor: st.bg, color: st.fg, border: `1px solid ${st.border}`,
                            }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_SECONDARY }}>
                          {formatRelative(row.updated_at)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, textAlign: "right" }}>
                          <Box sx={{ display: "inline-flex", gap: 0.25, alignItems: "center" }}>
                            <Tooltip title="Preview resolved playback" arrow>
                              <IconButton size="small" onClick={() => openPreview(row)}
                                sx={{ color: "#0891b2", "&:hover": { bgcolor: alpha("#0891b2", 0.1) } }}>
                                <PlayCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={row.is_active ? "Disable schedule" : "Enable schedule"} arrow>
                              <Switch size="small" checked={row.is_active}
                                onChange={() => toggleEnabled(row)} />
                            </Tooltip>
                            <Tooltip title="Edit schedule" arrow>
                              <IconButton size="small" onClick={() => navigate(`/menu-schedules/${row.group_id}/edit`)}
                                sx={{ color: "#1D4ED8", "&:hover": { bgcolor: alpha("#1D4ED8", 0.1) } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Remove schedule" arrow>
                                <IconButton size="small" onClick={() => { setToDelete(row); setOpenDelete(true); }}
                                  sx={{ color: "#EF4444", "&:hover": { bgcolor: alpha("#EF4444", 0.1) } }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filtered.length > rowsPerPage && (
            <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
              <Pagination
                count={filtered.length} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_e, p) => setPage(p)}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Preview resolved playback — reuses the central group resolver. */}
      <Dialog open={!!previewRow} onClose={() => setPreviewRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>Resolved playback</DialogTitle>
        <DialogContent>
          {previewRow && (
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 1.5 }}>
              What plays right now for <b>{previewRow.group_name}</b>{previewRow.company ? ` · ${previewRow.company.name}` : ""}.
            </Typography>
          )}
          {previewing ? (
            <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
          ) : preview ? (
            <Box>
              <Chip
                label={preview.override_active
                  ? "Emergency override active"
                  : `Rule: ${preview.rule}`}
                sx={{ fontWeight: 700, bgcolor: "#EEF2F6", mb: 1.5 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY, mb: 0.5 }}>Now playing</Typography>
              {(preview.playlists || []).length === 0 ? (
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>—</Typography>
              ) : (
                (preview.playlists as Array<{ id: number; name: string }>).map((p) => (
                  <Typography key={p.id} sx={{ fontSize: 13, color: TEXT_PRIMARY }}>• {p.name}</Typography>
                ))
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>No preview available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewRow(null)} sx={{ textTransform: "none" }}>Close</Button>
          {previewRow && (
            <Button variant="contained" onClick={() => navigate(`/menu-schedules/${previewRow.group_id}`)}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: NAVY, "&:hover": { bgcolor: NAVY_DARK } }}>
              Open schedule
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DeletePopup
        open={openDelete}
        handleCloseDelete={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Remove Menu Schedule"
        content={toDelete
          ? `Remove "${toDelete.playlist?.name}" from "${toDelete.group_name}"? This unschedules the menu; the playlist itself is not deleted.`
          : ""}
      />
    </Box>
  );
};

export default MenuScheduleList;
